/*
 * THE CITY, ASKED OF THE DEVICE (docs/5 § D260809-1521).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE FIELD IS NOT A TEXT BOX ANY MORE. A typed city is whatever somebody
 * types: a joke, a country, a neighbourhood, a misspelling, a blank. It is on a
 * card beside a real photograph of a real person and it is one of the few things
 * another member can use to decide whether writing to them makes sense — so the
 * one version of it worth having is the one the device answers.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THREE THINGS THIS DOES THAT THE OBVIOUS VERSION DOES NOT:
 *
 * 1. **It asks on a tap and never on load.** A geolocation prompt that appears
 *    because a screen mounted is the pattern browsers now penalise and people now
 *    refuse by reflex — and a refusal is remembered for the origin, so the cost of
 *    asking at the wrong moment is that the field can never work again on that
 *    device. The permission is spent when somebody has said what they want.
 * 2. **It asks for a coarse fix and accepts a warm one.** `enableHighAccuracy` off
 *    keeps the GPS asleep and answers from the radios in a fraction of the time; a
 *    fix from the last `PLACE.FIX_MAX_AGE_MS` is accepted as it stands, because a
 *    city does not change while somebody fills in a form. The second tap is
 *    usually instant.
 * 3. **It rounds before the fix leaves the device.** `PLACE.COORD_DECIMALS` is
 *    applied here, in this file, on this machine — so the exact position is used
 *    by nothing and sent to nobody. What travels is about a kilometre of
 *    ambiguity, which a city-level lookup cannot tell from the real thing.
 *
 * THE LOOKUP IS BigDataCloud's KEYLESS CLIENT ENDPOINT. It is free with no
 * account, no key and no quota, and their fair-use terms describe this exact
 * shape: the call is made by the client, from the platform's own geolocation, for
 * the device's current position. The alternative worth naming is Nominatim, which
 * is out on its published policy rather than on price — it forbids use as a
 * primary product feature, asks for one request a second, and expects the caller
 * not to be shared infrastructure. Because there is no key, there is also nothing
 * on this path for a Worker to proxy: the request would only add our own server to
 * the list of machines that saw somebody's coordinates.
 *
 * NOTHING HERE IS TRUSTED. What comes back is a suggestion for a field somebody
 * looks at before they save it, which is why this file has no validation beyond
 * "is there a city in it".
 */
import { formatPlace, PLACE, type Place } from '@dove/shared';

/*
 * THE TWO REFUSALS, AND THERE ARE ONLY TWO. Not exported: the field prints what it is
 * thrown rather than comparing against either, which is the arrangement `ApiError`
 * already has with a server message. `currentFix` below says why there are two.
 */
/** Permission was refused, or the platform has no location to give. */
const PLACE_DENIED_MESSAGE = 'Location is off.';
/** There was a fix, and nothing came back that names a place to live. */
const PLACE_UNKNOWN_MESSAGE = 'No city found.';

/**
 * The city the device is in, as the one line a card carries.
 *
 * Throws with a message the field can print. There is no third outcome: an empty
 * string would be a "success" the caller has to re-check, and a `null` is a
 * failure with the reason thrown away.
 */
export async function detectCity(): Promise<string> {
  const place = await detectPlace();
  const city = formatPlace(place);
  if (!city) throw new Error(PLACE_UNKNOWN_MESSAGE);
  return city;
}

async function detectPlace(): Promise<Place> {
  const fix = await currentFix();
  const at = {
    latitude: round(fix.coords.latitude),
    longitude: round(fix.coords.longitude),
  };
  return await lookUp(at);
}

/**
 * `getCurrentPosition`, as a promise.
 *
 * THE ERRORS COLLAPSE TO TWO SENTENCES, and the split is by what the person can do
 * about it rather than by the code: a refusal is a decision they made and can
 * unmake in their settings, and everything else — no signal, a timeout, a
 * platform without a receiver — is the same shrug from the field's point of view.
 */
function currentFix(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error(PLACE_DENIED_MESSAGE));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        reject(
          new Error(
            error.code === error.PERMISSION_DENIED
              ? PLACE_DENIED_MESSAGE
              : PLACE_UNKNOWN_MESSAGE,
          ),
        );
      },
      {
        /* City-level is the whole requirement, so the GPS stays off. */
        enableHighAccuracy: false,
        timeout: PLACE.FIX_TIMEOUT_MS,
        maximumAge: PLACE.FIX_MAX_AGE_MS,
      },
    );
  });
}

/** What the endpoint is asked for, and every field of the answer that is read. */
interface LookupResponse {
  city?: string;
  locality?: string;
  principalSubdivision?: string;
  countryCode?: string;
}

const LOOKUP_URL = 'https://api.bigdatacloud.net/data/reverse-geocode-client';

async function lookUp(at: {
  latitude: number;
  longitude: number;
}): Promise<Place> {
  const url = new URL(LOOKUP_URL);
  url.searchParams.set('latitude', String(at.latitude));
  url.searchParams.set('longitude', String(at.longitude));
  /* English, because the answer is written into a column every other member
     reads — see `formatPlace` for why the stored string has one language. */
  url.searchParams.set('localityLanguage', 'en');

  /*
   * `AbortController` AND A TIMER RATHER THAN `AbortSignal.timeout`, which is the
   * one-liner this wants to be. That helper landed in Safari 16, and Capacitor 7
   * supports iOS 15.6 — where reading it throws a TypeError from inside the `try`
   * below, so the field would fail with "no city found" on every attempt, on a
   * platform nobody testing this is holding. The verbose version has no floor.
   */
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), PLACE.LOOKUP_TIMEOUT_MS);

  let answer: LookupResponse;
  try {
    const response = await fetch(url, {
      /* NO COOKIES AND NO REFERRER. Nothing about this request identifies the
         account it is for, and it must stay that way: the coordinates are coarse
         precisely so that nobody downstream can build a trail out of them. */
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: stop.signal,
    });
    if (!response.ok) throw new Error(String(response.status));
    answer = (await response.json()) as LookupResponse;
  } catch {
    /* A timeout, an offline device and a rate limit are one sentence here. The
       person taps again or leaves the field empty; neither needs a diagnosis. */
    throw new Error(PLACE_UNKNOWN_MESSAGE);
  } finally {
    clearTimeout(timer);
  }

  /* `locality` is the smaller of the two names and is the fallback rather than the
     preference: outside a city proper, `city` is empty and `locality` is the town. */
  const city = (answer.city || answer.locality || '').trim();
  const countryCode = (answer.countryCode || '').trim();
  /*
   * A COUNTRY IS REQUIRED, AND IT IS THE OCEAN TEST. A fix in the sea comes back
   * with no city, no subdivision and no country — and a `locality` of
   * **"Atlantic Ocean"**, which the obvious version of this check accepts and puts
   * on somebody's card. Every inhabited place on earth has a country code, so its
   * absence is the honest signal that what came back is not somewhere to live.
   * Found by pointing the review harness at 30°S 30°W.
   */
  if (!city || !countryCode) throw new Error(PLACE_UNKNOWN_MESSAGE);

  return {
    city,
    ...(answer.principalSubdivision ? { region: answer.principalSubdivision } : {}),
    countryCode,
  };
}

/** Coarsened to `PLACE.COORD_DECIMALS` before it goes anywhere. */
function round(value: number): number {
  const scale = 10 ** PLACE.COORD_DECIMALS;
  return Math.round(value * scale) / scale;
}
